import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as AskAiIcon } from "../../../assets/icons/ask_ai.svg";
import { ReactComponent as RedoIcon } from "../../../assets/icons/redo.svg";
import { ReactComponent as UndoIcon } from "../../../assets/icons/undo.svg";
import useReaderStore from "../../../context/readerStore";
import { isFeatureAllowed } from "../../../services/drmPolicyService";
import { showErrorNotification } from "../../../services/notificationService";
import "./MobileBottomBar.css";

const MobileBottomBar = () => {
  const { t } = useTranslation();

  const {
    documentType,
    currentPage,
    totalPages,
    setCurrentPage,
    setCurrentPageEnd,
    drmPolicy,
    toggleReadingAssistantSidebar,
    isReadingAssistantSidebarOpen,
  } = useReaderStore();

  const highlightsAllowed = isFeatureAllowed(drmPolicy, "highlights");
  const underlineAllowed = isFeatureAllowed(drmPolicy, "underline");
  const undoRedoAllowed = highlightsAllowed || underlineAllowed;
  const aiAllowed = isFeatureAllowed(drmPolicy, "ai_features");
  const showUndoRedo =
    undoRedoAllowed && (documentType === "pdf" || documentType === "epub");

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [undoCooldownEnd, setUndoCooldownEnd] = useState(0);
  const [redoCooldownEnd, setRedoCooldownEnd] = useState(0);
  const [isPageInputActive, setIsPageInputActive] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const lastUndoDispatchRef = useRef(0);
  const lastRedoDispatchRef = useRef(0);
  const undoCooldownTimerRef = useRef(null);
  const redoCooldownTimerRef = useRef(null);
  const pageInputRef = useRef(null);
  const UNDO_REDO_DEBOUNCE_MS = 550;
  const COOLDOWN_MS = 550;

  useEffect(() => {
    const handleCapabilities = (e) => {
      const { canUndo: u, canRedo: r } = e.detail || {};
      setCanUndo(!!u);
      setCanRedo(!!r);
    };
    window.addEventListener("reader:can-undo", handleCapabilities);
    return () => {
      window.removeEventListener("reader:can-undo", handleCapabilities);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (undoCooldownTimerRef.current)
        clearTimeout(undoCooldownTimerRef.current);
      if (redoCooldownTimerRef.current)
        clearTimeout(redoCooldownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isPageInputActive || !pageInputRef.current) return;
    pageInputRef.current.focus();
    pageInputRef.current.select();
  }, [isPageInputActive]);

  const handlePageInputClick = useCallback(() => {
    setIsPageInputActive(true);
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  const handlePageInputCancel = useCallback(() => {
    setTimeout(() => {
      setIsPageInputActive(false);
      setPageInputValue("");
    }, 100);
  }, []);

  const handlePageInputSubmit = useCallback(() => {
    const pageNumber = parseInt(pageInputValue, 10);

    if (Number.isNaN(pageNumber)) {
      showErrorNotification(t("rightToolbar.pageInputInvalid"));
      return;
    }

    if (pageNumber < 1 || pageNumber > totalPages) {
      showErrorNotification(
        t("rightToolbar.pageInputOutOfRange", { total: totalPages }),
      );
      return;
    }

    setCurrentPageEnd(null);
    setCurrentPage(pageNumber);
    window.dispatchEvent(
      new CustomEvent("reader:go-to-page", { detail: { page: pageNumber } }),
    );

    setIsPageInputActive(false);
    setPageInputValue("");
  }, [pageInputValue, totalPages, setCurrentPage, setCurrentPageEnd, t]);

  const handlePageInputKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handlePageInputSubmit();
      } else if (event.key === "Escape") {
        event.preventDefault();
        handlePageInputCancel();
      }
    },
    [handlePageInputSubmit, handlePageInputCancel],
  );

  if (!documentType || isReadingAssistantSidebarOpen) {
    return null;
  }

  return (
    <div className="mobile-bottom-bar">
      <div className="mobile-bottom-bar-group mobile-bottom-bar-group--left">
        {showUndoRedo && (
          <>
            <button
              className={`mobile-bottom-button${!canUndo || undoCooldownEnd > 0 ? " is-disabled" : ""}${undoCooldownEnd > 0 ? " cooldown" : ""}`}
              onClick={() => {
                const now = Date.now();
                if (now - lastUndoDispatchRef.current < UNDO_REDO_DEBOUNCE_MS)
                  return;
                if (undoCooldownEnd > 0 || !canUndo) return;
                lastUndoDispatchRef.current = now;
                if (undoCooldownTimerRef.current)
                  clearTimeout(undoCooldownTimerRef.current);
                setUndoCooldownEnd(now + COOLDOWN_MS);
                undoCooldownTimerRef.current = setTimeout(
                  () => setUndoCooldownEnd(0),
                  COOLDOWN_MS,
                );
                window.dispatchEvent(new CustomEvent("reader:undo"));
              }}
              disabled={!canUndo || undoCooldownEnd > 0}
              aria-label={
                undoCooldownEnd > 0
                  ? t("floatingToolbar.undoCooldown")
                  : t("common.undo")
              }
              title={
                undoCooldownEnd > 0
                  ? t("floatingToolbar.undoCooldown")
                  : t("common.undo")
              }
            >
              <UndoIcon width={22} height={22} aria-hidden="true" />
              <span className="mobile-button-label">{t("common.undo")}</span>
            </button>

            <button
              className={`mobile-bottom-button${!canRedo || redoCooldownEnd > 0 ? " is-disabled" : ""}${redoCooldownEnd > 0 ? " cooldown" : ""}`}
              onClick={() => {
                const now = Date.now();
                if (now - lastRedoDispatchRef.current < UNDO_REDO_DEBOUNCE_MS)
                  return;
                if (redoCooldownEnd > 0 || !canRedo) return;
                lastRedoDispatchRef.current = now;
                if (redoCooldownTimerRef.current)
                  clearTimeout(redoCooldownTimerRef.current);
                setRedoCooldownEnd(now + COOLDOWN_MS);
                redoCooldownTimerRef.current = setTimeout(
                  () => setRedoCooldownEnd(0),
                  COOLDOWN_MS,
                );
                window.dispatchEvent(new CustomEvent("reader:redo"));
              }}
              disabled={!canRedo || redoCooldownEnd > 0}
              aria-label={
                redoCooldownEnd > 0
                  ? t("floatingToolbar.redoCooldown")
                  : t("common.redo")
              }
              title={
                redoCooldownEnd > 0
                  ? t("floatingToolbar.redoCooldown")
                  : t("common.redo")
              }
            >
              <RedoIcon width={22} height={22} aria-hidden="true" />
              <span className="mobile-button-label">{t("common.redo")}</span>
            </button>
          </>
        )}
      </div>

      <div className="mobile-bottom-bar-group mobile-bottom-bar-group--center">
        {totalPages > 0 &&
          (isPageInputActive ? (
            <form
              className="mobile-page-jump-form"
              onSubmit={(event) => {
                event.preventDefault();
                handlePageInputSubmit();
              }}
            >
              <input
                ref={pageInputRef}
                type="number"
                min="1"
                max={totalPages}
                value={pageInputValue}
                onChange={(event) => setPageInputValue(event.target.value)}
                onKeyDown={handlePageInputKeyDown}
                onBlur={handlePageInputCancel}
                className="mobile-page-jump-input"
                placeholder={t("rightToolbar.pageInputPlaceholder", {
                  min: 1,
                  max: totalPages,
                })}
                title={t("rightToolbar.pageInputTitle", {
                  min: 1,
                  max: totalPages,
                })}
                aria-label={t("rightToolbar.enterPageNumber")}
                enterKeyHint="go"
                inputMode="numeric"
              />
            </form>
          ) : (
            <button
              type="button"
              className="mobile-page-jump"
              onClick={handlePageInputClick}
              title={t("rightToolbar.enterPageNumber")}
              aria-label={t("rightToolbar.currentPageLabel", {
                current: currentPage,
              })}
            >
              <span className="mobile-page-jump-current">{currentPage}</span>
              <span className="mobile-page-jump-sep" aria-hidden="true">
                /
              </span>
              <span className="mobile-page-jump-total">{totalPages}</span>
            </button>
          ))}
      </div>

      <div className="mobile-bottom-bar-group mobile-bottom-bar-group--right">
        {/* Textbox - PDF only */}
        {/* {documentType === "pdf" && (
        <button
          className={`mobile-bottom-button${
            textboxEnabled ? " is-active" : ""
          }`}
          onClick={toggleTextboxEnabled}
          aria-pressed={textboxEnabled}
          aria-label={
            textboxEnabled
              ? t("floatingToolbar.textboxOff")
              : t("floatingToolbar.textboxOn")
          }
          title={
            textboxEnabled
              ? t("floatingToolbar.textboxOnLabel")
              : t("floatingToolbar.textboxOffLabel")
          }
        >
          <TextboxIcon width={22} height={22} aria-hidden="true" />
          <span className="mobile-button-label">
            {t("common.textbox") || "Text"}
          </span>
        </button>
      )} */}

        {aiAllowed && (
          <button
            type="button"
            className="mobile-bottom-button mobile-bottom-button--ai"
            onClick={toggleReadingAssistantSidebar}
            aria-pressed={isReadingAssistantSidebarOpen}
            aria-label={t("readingAssistant.title")}
            title={t("readingAssistant.title")}
          >
            <AskAiIcon
              className="preserve-original-fill"
              width={40}
              height={40}
              aria-hidden="true"
            />
            <span className="mobile-button-label">
              {t("readingAssistant.tabs.askAi")}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileBottomBar;
